import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCourses, getLocalLegends, getFriends } from '../api';
import { Avatar, PageHeader, RankBadge, Spinner, EmptyState, ErrorMsg } from '../components/UI';
import { useAuth } from '../context/AuthContext';

// ── Share entire region leaderboard ──────────────────────────────────────────
function ShareRegionButton({ regionType, regionVal, period, year, month, leaderboard }) {
  const [copied, setCopied] = useState(false);

  const buildShareText = () => {
    const MONTHS = ['January','February','March','April','May','June',
      'July','August','September','October','November','December'];
    const periodLabel =
      period === 'alltime' ? 'All-Time' :
      period === 'year' ? `${year}` :
      `${MONTHS[month - 1]} ${year}`;
    const typeEmoji = { city: '🏙️', state: '📍', country: '🌎' }[regionType];
    const top3 = leaderboard.slice(0, 3);
    const podium = top3.map((e, i) => `#${i + 1} ${e.username} (${e.rps?.toFixed(1)} RPS)`).join(' · ');
    return `${typeEmoji} Local Legends — ${regionVal} (${periodLabel})\n${podium}\nSee the full leaderboard at GolfBoard Scores: https://golfboardscores.com/local-legends`;
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    const text = buildShareText();
    try { await navigator.clipboard.writeText(text); }
    catch {
      const el = document.createElement('textarea');
      el.value = text; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex-shrink-0 flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-all duration-200 ${
        copied
          ? 'bg-green-50 border-green-300 text-green-700 font-semibold'
          : 'bg-white border-gray-200 text-gray-400 hover:border-green-400 hover:text-green-700'
      }`}
      title="Share this leaderboard"
    >
      {copied ? <>✓ Copied!</> : <>🔗 Share</>}
    </button>
  );
}

// ── Per-player share button ───────────────────────────────────────────────────
function SharePlayerButton({ entry, regionType, regionVal, period, year, month }) {
  const [copied, setCopied] = useState(false);

  const MONTHS = ['January','February','March','April','May','June',
    'July','August','September','October','November','December'];
  const periodLabel =
    period === 'alltime' ? 'All-Time' :
    period === 'year' ? `${year}` :
    `${MONTHS[month - 1]} ${year}`;
  const typeEmoji = { city: '🏙️', state: '📍', country: '🌎' }[regionType];
  const text = `${typeEmoji} ${entry.username} is ranked #${entry.rank} in ${regionVal} (${periodLabel}) on GolfBoard Scores!\n${entry.rps?.toFixed(1)} RPS · ${entry.total_rounds} rounds · ${entry.unique_courses} course${entry.unique_courses !== 1 ? 's' : ''}\nhttps://golfboardscores.com/local-legends`;

  const handleCopy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(text); }
    catch {
      const el = document.createElement('textarea');
      el.value = text; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex-shrink-0 flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border transition-all duration-200 ${
        copied
          ? 'bg-green-50 border-green-300 text-green-700 font-semibold'
          : 'bg-white border-gray-200 text-gray-300 hover:border-green-400 hover:text-green-700'
      }`}
      title="Share this player's ranking"
    >
      {copied ? '✓' : '🔗'}
    </button>
  );
}

// ── Stat pill ─────────────────────────────────────────────────────────────────
function StatPill({ label, value }) {
  return (
    <div className="flex flex-col items-center bg-green-50 rounded-lg px-3 py-1.5 min-w-[60px]">
      <span className="text-xs font-bold text-green-900">{value ?? '—'}</span>
      <span className="text-xs text-gray-400 leading-tight">{label}</span>
    </div>
  );
}

// ── RPS explanation tooltip ───────────────────────────────────────────────────
function RpsInfo() {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setShow(s => !s)}
        className="ml-1 text-gray-400 hover:text-green-700 text-xs border border-gray-200 rounded-full w-4 h-4 inline-flex items-center justify-center leading-none"
        title="What is RPS?"
      >?</button>
      {show && (
        <div className="absolute left-0 top-6 z-20 w-64 bg-white border border-amber-100 rounded-xl shadow-lg p-3 text-xs text-gray-600 fade-in">
          <strong className="text-green-900">Round Performance Score (RPS)</strong> is a composite ranking metric based on total rounds, verified rounds, unique courses played, and best scores. Higher is better.
          <button onClick={() => setShow(false)} className="ml-2 text-gray-400 hover:text-gray-600">✕</button>
        </div>
      )}
    </div>
  );
}

// ── Single region leaderboard card ───────────────────────────────────────────
function RegionCard({ regionType, regionVal, period, year, month, friendIds, friendsOnly }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError('');
    getLocalLegends(regionType, regionVal, period, year, month)
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [regionType, regionVal, period, year, month]);

  const rawLeaderboard = data?.leaderboard || [];
  // When friends filter is on, show only friend entries (+ current user ranks preserved)
  const leaderboard = friendsOnly && friendIds
    ? rawLeaderboard.filter(e => friendIds.has(e.user_id))
    : rawLeaderboard;

  return (
    <div className="card overflow-hidden">
      {/* Card header */}
      <div
        className="px-5 py-4 flex items-center justify-between hover:bg-amber-50/50 transition-colors cursor-pointer"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">
            {regionType === 'city' ? '🏙️' : regionType === 'state' ? '📍' : '🌎'}
          </span>
          <div>
            <h3 className="font-serif font-semibold text-green-900 text-base capitalize">{regionVal}</h3>
            <p className="text-xs text-gray-400 capitalize">{regionType}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!loading && leaderboard.length > 0 && (
            <ShareRegionButton
              regionType={regionType} regionVal={regionVal}
              period={period} year={year} month={month}
              leaderboard={leaderboard}
            />
          )}
          {loading && <Spinner size="sm" />}
          <span className={`text-gray-400 text-sm transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>▾</span>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-amber-50 px-4 pb-4 pt-2 fade-in">
          {error ? (
            <p className="text-sm text-red-500 py-3">{error}</p>
          ) : loading ? (
            <div className="flex justify-center py-6"><Spinner /></div>
          ) : leaderboard.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No scores recorded in this region yet.</p>
          ) : (
            <div className="space-y-1">
              {leaderboard.map(entry => (
                <div key={entry.user_id} className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-amber-50/60 transition-colors">
                  <RankBadge rank={entry.rank} />
                  <Link
                    to={`/profile/${entry.user_id}`}
                    className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                  >
                    <Avatar url={entry.avatar_url} username={entry.username} size="sm" />
                    <span className="font-medium text-green-900 truncate text-sm">{entry.username}</span>
                  </Link>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-bold text-green-800">{entry.rps?.toFixed(1)}</span>
                      <span className="text-xs text-gray-400">RPS</span>
                    </div>
                    <div className="hidden sm:flex items-center gap-1.5">
                      <StatPill label="Rounds" value={entry.total_rounds} />
                      <StatPill label="Courses" value={entry.unique_courses} />
                      {entry.best_over_under != null && (
                        <StatPill
                          label="Best"
                          value={entry.best_over_under === 0 ? 'E' : entry.best_over_under > 0 ? `+${entry.best_over_under}` : `${entry.best_over_under}`}
                        />
                      )}
                    </div>
                  </div>
                  <SharePlayerButton
                    entry={entry} regionType={regionType} regionVal={regionVal}
                    period={period} year={year} month={month}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Filters bar ───────────────────────────────────────────────────────────────
function FiltersBar({ period, setPeriod, year, setYear, month, setMonth, friendsOnly, setFriendsOnly }) {
  const now = new Date();
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const yearOptions = [];
  for (let y = now.getFullYear(); y >= 2024; y--) yearOptions.push(y);

  return (
    <div className="flex flex-wrap items-center gap-2 mb-5">
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {[['alltime','All Time'],['year','This Year'],['month','Monthly']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setPeriod(val)}
            className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-all ${
              period === val ? 'bg-white text-green-900 shadow-sm' : 'text-gray-500 hover:text-green-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {period !== 'alltime' && (
        <select className="input-field py-1.5 text-sm w-auto" value={year} onChange={e => setYear(Number(e.target.value))}>
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      )}
      {period === 'month' && (
        <select className="input-field py-1.5 text-sm w-auto" value={month} onChange={e => setMonth(Number(e.target.value))}>
          {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
      )}
      <button
        onClick={() => setFriendsOnly(f => !f)}
        className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-all ${
          friendsOnly
            ? 'bg-green-700 text-white border-green-700'
            : 'bg-white text-gray-500 border-gray-200 hover:border-green-400 hover:text-green-800'
        }`}
      >
        🤝 Friends Only
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function LocalLegendsPage() {
  const { user } = useAuth();
  const now = new Date();
  const [regionType, setRegionType] = useState('city');
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('alltime');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [friendsOnly, setFriendsOnly] = useState(false);
  const [friendIds, setFriendIds] = useState(null); // Set<uuid> once loaded
  const [allRegions, setAllRegions] = useState({ city: [], state: [], country: [] });
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [regionError, setRegionError] = useState('');

  useEffect(() => {
    getCourses()
      .then(courses => {
        const cities = new Set();
        const states = new Set();
        const countries = new Set();
        courses.forEach(c => {
          if (c.city?.trim()) cities.add(c.city.trim());
          if (c.state?.trim()) states.add(c.state.trim());
          if (c.country?.trim()) countries.add(c.country.trim());
        });
        setAllRegions({ city: [...cities].sort(), state: [...states].sort(), country: [...countries].sort() });
      })
      .catch(() => setRegionError('Could not load region data.'))
      .finally(() => setLoadingRegions(false));
  }, []);

  // Load friend IDs (include self) so RegionCard can filter client-side
  useEffect(() => {
    if (!user) return;
    getFriends()
      .then(data => {
        const ids = new Set((data?.friends || []).map(f => f.user.id));
        ids.add(user.id);
        setFriendIds(ids);
      })
      .catch(() => setFriendIds(new Set(user ? [user.id] : [])));
  }, [user]);

  const regions = allRegions[regionType] || [];
  const filteredRegions = search.trim()
    ? regions.filter(r => r.toLowerCase().includes(search.toLowerCase().trim()))
    : regions;

  const typeLabel = { city: 'Cities', state: 'States', country: 'Countries' }[regionType];
  const typeIcon = { city: '🏙️', state: '📍', country: '🌎' }[regionType];

  return (
    <div className="page-container">
      <PageHeader
        title="🏅 Local Legends"
        subtitle="Top 5 golfers ranked by region — city, state, or country"
      />

      {/* Explainer */}
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6 flex gap-3 items-start">
        <span className="text-2xl flex-shrink-0">🏅</span>
        <div className="text-sm text-amber-900">
          <strong>Local Legends</strong> ranks the top 5 golfers in each region based on their{' '}
          <span className="font-medium">Round Performance Score (RPS)</span>
          <RpsInfo />
          {' '}— a metric combining rounds played, verified rounds, unique courses, and best score.
          Use the <strong>🔗 Share</strong> button on any card to copy a shareable summary, or share an individual player's ranking with the icon next to their name.
        </div>
      </div>

      {/* Region type tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-4">
        {[['city','🏙️ Cities'],['state','📍 States'],['country','🌎 Countries']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => { setRegionType(val); setSearch(''); }}
            className={`flex-1 text-sm font-medium py-2 rounded-lg transition-all ${
              regionType === val ? 'bg-white text-green-900 shadow-sm' : 'text-gray-500 hover:text-green-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <FiltersBar
        period={period} setPeriod={setPeriod}
        year={year} setYear={setYear}
        month={month} setMonth={setMonth}
        friendsOnly={friendsOnly} setFriendsOnly={setFriendsOnly}
      />

      {/* Search */}
      <div className="relative mb-5">
        <input
          type="text"
          className="input-field pl-9"
          placeholder={`🔍 Search ${typeLabel.toLowerCase()}...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm">✕</button>
        )}
      </div>

      {regionError && <ErrorMsg message={regionError} />}

      {loadingRegions ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : filteredRegions.length === 0 ? (
        <EmptyState
          icon={typeIcon}
          title={search ? `No ${typeLabel.toLowerCase()} match "${search}"` : `No ${typeLabel.toLowerCase()} found`}
          message={search ? 'Try a different search term.' : 'Add courses to see region leaderboards populate.'}
        />
      ) : (
        <>
          <p className="text-xs text-gray-400 mb-4">
            {filteredRegions.length} {typeLabel.toLowerCase()} · {friendsOnly ? 'showing friends only' : 'showing top 5 per region'}
          </p>
          <div className="space-y-3">
            {filteredRegions.map(regionVal => (
              <RegionCard
                key={`${regionType}-${regionVal}`}
                regionType={regionType}
                regionVal={regionVal}
                period={period}
                year={year}
                month={month}
                friendIds={friendIds}
                friendsOnly={friendsOnly}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}